import { createFileRoute, Navigate, redirect, useSearch } from '@tanstack/react-router';
import {
  canReachWorkOrders,
  SubscriptionStatus,
  type TenantCampaignWithCapabilitiesDto,
} from '@signalvote/shared';
import { useAuth } from '@/contexts/AuthContext';
import { myCampaignsQuery } from '@/hooks/queries';
import { useCampaignStore } from '@/stores';

const SUBSCRIBED: ReadonlySet<SubscriptionStatus> = new Set([SubscriptionStatus.Active, SubscriptionStatus.PastDue]);

/** A campaign without a subscription that has a Work Order lands among its Work Orders; every other campaign lands on Home. */
function landsOnWorkOrders(campaign: TenantCampaignWithCapabilitiesDto, isPlatformAdmin: boolean) {
  const reachable = canReachWorkOrders({
    isPlatformAdmin,
    isFullAdmin: useCampaignStore.getState().isFullAdmin(),
    workOrdersEnabled: campaign.workOrdersEnabled,
  });
  return reachable && campaign.hasWorkOrders && !SUBSCRIBED.has(campaign.subscriptionStatus);
}

// /app is the sign-in default; a redirect the person signed in with goes there instead. Search params
// survive the redirect (e.g. ?notice=...). It never opens the Express steps: an Express customer comes
// back through their link, and anyone else on their campaign would be stuck there. With no campaign
// chosen yet, the guard waits: the campaign gate picks one and reloads here, so Home never shows first.
export const Route = createFileRoute('/app/')({
  beforeLoad: async ({ context, search }) => {
    if (!useCampaignStore.getState().selectedCampaign) return;
    // The stored selection is as of sign-in; Work Orders or a subscription since then changes the landing.
    await context.queryClient.fetchQuery({ ...myCampaignsQuery(), staleTime: 0 }).catch(() => undefined);
    const campaign = useCampaignStore.getState().selectedCampaign;
    if (!campaign) return;
    const to = landsOnWorkOrders(campaign, !!context.auth.user?.isPlatformAdmin) ? '/app/work-orders' : '/app/home';
    throw redirect({ to, search });
  },
  component: AppIndex,
});

function AppIndex() {
  const campaign = useCampaignStore((s) => s.selectedCampaign);
  const search = useSearch({ strict: false });
  const { user } = useAuth();
  if (!campaign) return null;
  const to = landsOnWorkOrders(campaign, !!user?.isPlatformAdmin) ? '/app/work-orders' : '/app/home';
  return <Navigate to={to} search={search} replace />;
}
